import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/endpoints'
import { getAccessToken, getRefreshToken } from '@/api/client'
import type { AuthUser } from '@/api/types'

export const useAuthStore = defineStore('auth', () => {
  const token = ref(getAccessToken())
  const refresh = ref(getRefreshToken())
  const user = ref<AuthUser | null>(null)
  const loading = ref(false)
  const error = ref('')

  const isAuthenticated = computed(() => Boolean(token.value))
  const isAdmin = computed(() => user.value?.is_superuser || user.value?.primary_role_slug === 'admin')
  const isObserver = computed(() => user.value?.primary_role_slug === 'observer')
  const canRun = computed(() => !isObserver.value && isAuthenticated.value)
  const canManageProviders = computed(() => isAdmin.value)
  const canViewStats = computed(() => isAdmin.value)

  async function login(identifier: string, password: string): Promise<boolean> {
    loading.value = true
    error.value = ''
    try {
      const tokens = await authApi.login(identifier, password)
      authApi.saveTokens(tokens.access, tokens.refresh)
      token.value = tokens.access
      refresh.value = tokens.refresh
      try {
        user.value = await authApi.me()
      } catch {
        user.value = null
      }
      return true
    } catch (e) {
      error.value = (e as Error).message
      return false
    } finally {
      loading.value = false
    }
  }

  async function refreshUser(): Promise<void> {
    if (!token.value) return
    try {
      user.value = await authApi.me()
    } catch {
      logout()
    }
  }

  function logout(): void {
    authApi.logoutLocal()
    token.value = ''
    refresh.value = ''
    user.value = null
  }

  return {
    token,
    refresh,
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    isObserver,
    canRun,
    canManageProviders,
    canViewStats,
    login,
    refreshUser,
    logout,
  }
})
